import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { BehaviorSubject } from 'rxjs'
import { CountryCode, LocaleCode, SupportedLocales } from 'src/app/interfaces/locale'
import en from 'src/assets/locale/en.json'
import ja from 'src/assets/locale/ja.json'
import zhHant from 'src/assets/locale/zh-hant.json'
import { environment } from 'src/environments/environment'
import { isAdsenseBot } from 'src/utils/bot'
import { LogService } from '../log/log.service'

export const supportedLocales: SupportedLocales = {
  en,
  zhHant,
  ja,
  // Add more languages as needed
}

type SupportedLocaleKeys = keyof SupportedLocales
type CustomValue = Partial<Record<Exclude<LocaleCode, 'ko'>, string>>

@Injectable({
  providedIn: 'root',
})
export class LocaleService {
  constructor(private logService: LogService, private translate: TranslateService) {
    this.init()
  }

  // Precompute the regex mappings for each locale
  private regexMappings: { [locale in LocaleCode]?: { [regex: string]: string } } = {}

  private _locale: LocaleCode = isAdsenseBot
    ? LocaleCode.ko
    : (localStorage.getItem('locale') as LocaleCode | null) ?? this.getLocaleWithLocalProperty()
  locale$ = new BehaviorSubject(this._locale)

  get locale(): LocaleCode {
    return this._locale
  }
  set locale(locale: LocaleCode) {
    if (isAdsenseBot) {
      this._locale = LocaleCode.ko
      return
    }
    const isLocaleChanged = this._locale !== locale
    this._locale = locale
    if (locale !== LocaleCode.ko) {
      environment.domainName = this.getLocaleUrl(environment.domainName)
      environment.webUrl = this.getLocaleUrl(environment.webUrl)
      environment.cabinetWebUrl = this.getLocaleUrl(environment.cabinetWebUrl)
    }
    this.setLocaleLogProperty(locale)
    this.translate.use(locale)
    localStorage.setItem('locale', locale)
    if (isLocaleChanged) {
      location.reload()
    }
  }

  get isKo(): boolean {
    return this.locale === LocaleCode.ko
  }

  setInitialLocale(isLoggedIn: boolean, languageCode?: LocaleCode): void {
    if (isLoggedIn && languageCode) {
      this.locale = languageCode.replace(/-([a-z])/g, (match, group1) => {
        return group1.toUpperCase()
      }) as LocaleCode
      return
    }
    const result = (localStorage.getItem('locale') as LocaleCode | null) ?? this.getLocaleWithLocalProperty()
    this.locale = result
    this.locale$.next(result)
  }

  getLocaleString(key: string, customValues?: CustomValue): string {
    if (this.locale === LocaleCode.ko) return key

    if (customValues && customValues[this.locale]) {
      return customValues[this.locale] ?? customValues.en ?? key
    }

    const localeString = supportedLocales?.[this.locale]
    if (!localeString) return supportedLocales[LocaleCode.en][key] ?? key

    const regexMap = this.regexMappings[this.locale]
    // 만약 정규식 없는 키라면({value} 없다면) 바로 반환
    if (regexMap?.[`^${key}$`] !== undefined) {
      return regexMap[`^${key}$`]
    }
    for (const regex in regexMap) {
      const keyRegex = new RegExp(regex)
      const matches = key.match(keyRegex)
      if (matches) {
        return regexMap[regex].replace('{value}', matches[1])
      }
    }

    const result = localeString?.[key]
    return result ?? supportedLocales[LocaleCode.en][key] ?? key
  }

  getParsedLocaleString(content: string | number, value: string, placeholder: string = '{value}') {
    const text = this.getLocaleString(value)
    return text.replace(placeholder, content.toString())
  }
  /**
   * 이미지에 -${locale} 붙이기
   * ex. image.png -> image-en.png
   */
  getLocaleSrc(src: string): string {
    if (this.locale === LocaleCode.ko) return src
    const regex = /png|jpg|jpeg|gif|svg/
    const [imageName, extension] = src.split('.')
    if (extension && regex.test(extension)) {
      return `${imageName}-${this.locale}.${extension}`
    }
    return src
  }

  getLocaleUrl(url: string, localeCode?: LocaleCode): string {
    const locale = localeCode ?? this.locale
    if (url.includes('localhost')) return url
    if (locale === LocaleCode.ko) return url

    // 이미 locale이 붙어있는 경우는 제거하고 다시 생성 (locale이 변경될 경우도 있기 때문)
    if (/(en|tw|jp)(.|-|_)/.test(url)) {
      url = url.replace(/(en|tw|jp)(.|-|_)/, '')
    }

    const urlLocale = locale.replace('zhHant', 'tw').replace('ja', 'jp')

    // branch link인 경우 invite link로 통일
    if (url.includes('types.app.link')) {
      // const parts = url.split('/')
      // const lastPart = parts[parts.length - 1] 기존 정책 -> 나라마다 branch 링크 생성
      const lastPart = 'invite'
      return `https://types.app.link/${urlLocale}_${lastPart}`
    }
    const { webUrl, cabinetWebUrl } = environment
    const protocolRegex = /^(?:https?:\/\/)?/
    const hasProtocol = url.match(protocolRegex)?.[0]
    const webUrlProtocol = webUrl?.match(protocolRegex)?.[0]
    const cabinetUrlProtocol = cabinetWebUrl?.match(protocolRegex)?.[0]
    const webUrlHost = webUrl?.replace(protocolRegex, '')
    const cabinetUrlHost = cabinetWebUrl?.replace(protocolRegex, '')
    if (!webUrlHost || !cabinetUrlHost) return url
    if (url.includes(webUrlHost) || url.includes(cabinetUrlHost)) {
      // test.headmost.app 같은 경우에는 en.test.headmost.app이 안되서 en-test.headmost.app로 변경
      const localeString = urlLocale + (url.split('.').length > 2 ? '-' : '.')
      const protocol = hasProtocol ? (url.includes(webUrlHost) ? webUrlProtocol : cabinetUrlProtocol) : ''
      if (protocol) {
        return url.replace(protocol, `${protocol}${localeString}`)
      }
      return `${localeString}${url}`
    }
    return url
  }

  getLocaleName(firstName?: string, lastName?: string): string {
    if (this.locale === LocaleCode.ko) {
      return `${lastName ?? ''}${firstName ?? ''}`
    }
    return firstName ? `${firstName}${lastName ? ' ' + lastName : ''}` : lastName ?? ''
  }

  async getCountry(): Promise<CountryCode> {
    try {
      const res = await fetch('https://api.country.is')
      const countryRes = await res.json()
      const countryCodes = Object.keys(CountryCode)
      if (countryRes?.country && countryCodes.includes(countryRes.country)) {
        return countryRes.country
      }
    } catch (error) {
      console.error(error)
    }
    if (this.locale === LocaleCode.ko) return CountryCode.KR
    return CountryCode.US
  }

  async getPageTitle(key: string, interpolateParams?: Object): Promise<string> {
    const title = this.translate.get(key, interpolateParams).toPromise()
    return title
  }

  /**
   * url, navigator 정보로 locale 설정
   * ------- (이하 2024.11.08 변경)
   * 언어별로 노출되는 컨텐츠가 다른데, navigator에 따라 막 바꾸면 안되는 경우가 있어서
   * host에 따라서만 locale 설정하도록 수정
   */
  private getLocaleWithLocalProperty(): LocaleCode {
    let result: LocaleCode = LocaleCode.ko
    const isEnHost = location.host.startsWith('en')
    const isTwHost = location.host.startsWith('tw')
    const isJpHost = location.host.startsWith('jp')
    // domain에 en 붙어있으면 영어로. 이게 최상위 우선순위
    if (isEnHost) {
      result = LocaleCode.en
    } else if (isTwHost) {
      result = LocaleCode.zhHant
    } else if (isJpHost) {
      result = LocaleCode.ja
    } else {
      result = LocaleCode.ko
      // const isLanguageKo = navigator.language.includes('ko')
      // const isLanguageTw =
      //   navigator.language.toLowerCase() === 'zh-tw' || navigator.language.toLowerCase() === 'zh-hant'
      // result = isLanguageKo ? LocaleCode.ko : isLanguageTw ? LocaleCode.zhHant : LocaleCode.en
    }
    return result
  }

  private setLocaleLogProperty(locale: LocaleCode) {
    this.logService.setUserConfig({ locale })
    this.logService.setSuperProperty('locale', locale)
  }

  private escapeRegExpPreservingPlaceholders(string: string) {
    // Preserve placeholders by temporarily replacing them with unique markers
    const placeholderPattern = /\(\.\*\)/g
    const placeholderMarker = '__PLACEHOLDER__'

    let escapedString = string.replace(placeholderPattern, placeholderMarker)

    // Escape special characters in the string, ignoring the markers
    escapedString = escapedString.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

    // Replace the markers with the original placeholders
    escapedString = escapedString.replace(new RegExp(placeholderMarker, 'g'), '(.*)')

    return escapedString
  }

  private stringToRegExp(string: string) {
    const escapedString = this.escapeRegExpPreservingPlaceholders(string)
    return new RegExp(`^${escapedString}$`)
  }

  private init() {
    Object.keys(supportedLocales).forEach((locale) => {
      this.regexMappings[locale as SupportedLocaleKeys] = {}
      const localeString = supportedLocales[locale as SupportedLocaleKeys]
      Object.keys(localeString).forEach((key) => {
        const regex = this.stringToRegExp(key.replace('{value}', '(.*)')).source
        this.regexMappings[locale as LocaleCode]![regex] = localeString[key]
      })
    })
  }
}
